import { useEffect } from 'react';
import { extractClosestEdge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import { reorderWithEdge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/util/reorder-with-edge';
import { monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { useExperienceAbort } from '@atlassian/jira-experience-tracker/src/ui/experience-abort/index.tsx';
import { useExperienceStart } from '@atlassian/jira-experience-tracker/src/ui/experience-start/index.tsx';
import { useRankNavigationItem } from '@atlassian/jira-horizontal-nav-rank-item-service/src/services/index.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';

type Tab = {
	id: string;
	typeKey: string | null | undefined;
	appId: string | null | undefined;
	dataId: string;
};

// exported for testing
export const PACKAGE_NAME = 'jiraNavigationKitRankItem';
export const REORDER_VIEW_EXPERIENCE = { experience: 'reorderView' };

const BEFORE = 'before';
const AFTER = 'after';
type Placement = typeof BEFORE | typeof AFTER;
export const useDroppableTabList = (
	tabs: Tab[],
	containerNavigationId: string,
	scopeId: string,
) => {
	const onStartExperience = useExperienceStart({
		...REORDER_VIEW_EXPERIENCE,
		analyticsSource: PACKAGE_NAME,
	});
	const onExperienceAbort = useExperienceAbort(REORDER_VIEW_EXPERIENCE);
	const [rankNavigationItem] = useRankNavigationItem();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	useEffect(
		() =>
			monitorForElements({
				canMonitor({ source }) {
					return source.data.type === 'tab';
				},
				onDrop({ source, location }) {
					onStartExperience();
					const destination = location.current.dropTargets[0];
					if (!destination) {
						onExperienceAbort('Dropped outside of drop targets');
						return;
					}

					if (source.data.type === 'tab' && source.data.id !== destination.data.id) {
						const sourceTabIndex = tabs.findIndex((tab) => tab.id === source.data.id);

						let targetTabIndex;
						let closestEdgeOfTarget;
						if (destination.data.isMoreMenu) {
							// assume no pagination and move it to the bottom of the list
							targetTabIndex = tabs.length - 1;
							closestEdgeOfTarget = 'bottom' as const;
						} else {
							targetTabIndex = tabs.findIndex((tab) => tab.id === destination.data.id);
							closestEdgeOfTarget = extractClosestEdge(destination.data);
						}
						const reorderedTabs = reorderWithEdge({
							list: tabs,
							startIndex: sourceTabIndex,
							indexOfTarget: targetTabIndex,
							closestEdgeOfTarget,
							axis: destination.data.isOverflowing ? 'vertical' : 'horizontal',
						});

						if (reorderedTabs && tabs.every((value, index) => value === reorderedTabs[index]))
							return;

						const placement: Placement =
							closestEdgeOfTarget === 'left' || closestEdgeOfTarget === 'top' ? BEFORE : AFTER;

						const { id, typeKey, appId, dataId } = tabs[sourceTabIndex];
						const beforeItemId = placement === BEFORE ? tabs[targetTabIndex].id : null;
						const afterItemId = placement === AFTER ? tabs[targetTabIndex].id : null;

						if (
							id.includes('optimisticId') ||
							(beforeItemId && beforeItemId.includes('optimisticId')) ||
							(afterItemId && afterItemId.includes('optimisticId'))
						)
							return;

						fireUIAnalytics(
							createAnalyticsEvent({}),
							'navigationItem dragged',
							'projectNavigationTab',
							{
								itemType: typeKey && typeKey.toLowerCase(),
								itemId: appId,
								draggedFromMore: Boolean(source.data.isOverflowing),
								draggedToMore: Boolean(
									destination.data.isOverflowing || destination.data.isMoreMenu,
								),
							},
						);

						rankNavigationItem({
							placement,
							sourceTabIndex,
							targetTabIndex,
							id,
							beforeItemId,
							afterItemId,
							dataId,
							actionType: 'dragged',
							containerNavigationId,
							scopeId,
						});
					}
				},
			}),
		[
			tabs,
			rankNavigationItem,
			onStartExperience,
			onExperienceAbort,
			containerNavigationId,
			scopeId,
			createAnalyticsEvent,
		],
	);
};
